import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiKey } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { User, Role } from '../../types';
import { userService, UpdateUserData } from '../../services/userService';
import { rolesService } from '../../services/rolesService';
import { showConfirm } from '../../utils/sweetAlert';

// Importar los componentes estilizados compartidos
import {
  ActionButton,
  ActionButtons,
  Button,
  ButtonGroup,
  Form,
  FormGroup,
  IconButton,
  Input,
  Label,
  Modal,
  ModalContent,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  PaginationControls,
  PaginationEllipsis,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableWrapper
} from '../../components/shared/StyledComponents';

// Convertir los iconos a React.ComponentType
const Plus = FiPlus as React.ComponentType;
const Edit = FiEdit as React.ComponentType;
const Trash = FiTrash2 as React.ComponentType;
const Key = FiKey as React.ComponentType;

const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [activeFilter, setActiveFilter] = useState<'admin' | 'customer' | 'staff'>('admin');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    dni: '',
    password: '',
    role: 'customer' as 'customer' | 'admin',
    role_id: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5); // 5 usuarios por página como solicitado
  
  // Calcular usuarios para la página actual
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  
  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  useEffect(() => {
    filterUsers(activeFilter);
  }, [users, activeFilter]);
  
  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await rolesService.getRoles();
      setRoles(data);
    } catch (e) {
      //
    }
  };

  const filterUsers = (filter: 'admin' | 'customer' | 'staff') => {
    setActiveFilter(filter);
    setFilteredUsers(users.filter(user => {
      if (filter === 'admin') return user.role === 'admin';
      if (filter === 'staff') return user.role === 'customer' && !!user.role_id;
      return user.role === 'customer' && !user.role_id;
    }));
    setCurrentPage(1);
  };
  
  // Funciones de paginación
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  
  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };
  
  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      dni: '',
      password: '',
      role: 'customer',
      role_id: ''
    });
    setSelectedRole('');
    setShowModal(true);
  };
  
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      dni: user.dni,
      password: '',
      role: user.role,
      role_id: user.role_id || ''
    });
    setSelectedRole(user.role === 'admin' ? 'admin' : (user.role_id || ''));
    setShowModal(true);
  };
  
  const handleResetPassword = (user: User) => {
    setEditingUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };
  
  const handleDeleteUser = async (userId: string) => {
    const confirmed = await showConfirm('¿Estás seguro de que quieres eliminar este usuario?');
    if (confirmed) {
      try {
        await userService.deleteUser(userId);
        setUsers(users.filter(u => u.id !== userId));
        toast.success('Usuario eliminado correctamente');
      } catch (error) {
        toast.error('Error al eliminar usuario');
        console.error(error);
      }
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar que el DNI tenga exactamente 8 dígitos si está presente
    if (formData.dni && formData.dni.length !== 8) {
      toast.error('El DNI debe tener exactamente 8 dígitos');
      return;
    }

    // Validar contraseña mínima al crear
    if (!editingUser && (!formData.password || formData.password.length < 6)) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      if (editingUser) {
        // Actualizar usuario
        const updatePayload: UpdateUserData = {
          name: formData.name,
          email: formData.email,
          dni: formData.dni,
          role: selectedRole === 'admin' ? 'admin' : 'customer',
          role_id: selectedRole === 'admin' ? undefined : (selectedRole || undefined)
        };
        await userService.updateUser(editingUser.id, updatePayload);
        
        setUsers(users.map(u => {
          if (u.id !== editingUser.id) return u;
          const isAdmin = selectedRole === 'admin';
          const selectedRoleObj = roles.find(r => String(r.id) === selectedRole);
          return {
            ...u,
            name: formData.name,
            email: formData.email,
            dni: formData.dni,
            role: isAdmin ? 'admin' : 'customer',
            role_id: isAdmin ? undefined : (selectedRole || undefined),
            role_name: isAdmin ? undefined : (selectedRoleObj?.name || undefined)
          };
        }));
        
        toast.success('Usuario actualizado correctamente');
      } else {
        // Crear usuario
        const result = await userService.createUser({
          name: formData.name,
          email: formData.email,
          dni: formData.dni,
          password: formData.password,
          role: selectedRole === 'admin' ? 'admin' : 'customer',
          role_id: selectedRole === 'admin' ? undefined : (selectedRole || undefined)
        });
        
        // Recargar usuarios para obtener el nuevo
        await loadUsers();
        
        toast.success('Usuario creado correctamente');
      }
      
      setShowModal(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Error al guardar usuario';
      toast.error(message);
      console.error(error);
    }
  };
  
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUser) return;
    
    try {
      await userService.resetPassword(editingUser.id, newPassword);
      toast.success('Contraseña actualizada correctamente');
      setShowPasswordModal(false);
    } catch (error) {
      toast.error('Error al actualizar contraseña');
      console.error(error);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Validación específica para el campo DNI
    if (name === 'dni') {
      // Solo permitir números y máximo 8 dígitos
      const numericValue = value.replace(/\D/g, ''); // Remover todo lo que no sea dígito
      if (numericValue.length <= 8) {
        setFormData(prev => ({
          ...prev,
          [name]: numericValue
        }));
      }
      return;
    }
    
    if (name === 'selectedRole') {
      setSelectedRole(value);
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <ActionButton onClick={handleAddUser}>
          <Plus /> Agregar Usuario
        </ActionButton>
        
        <ButtonGroup>
          <Button 
            variant={activeFilter === 'admin' ? 'primary' : 'secondary'}
            onClick={() => filterUsers('admin')}
          >
            ADMINS
          </Button>
          <Button 
            variant={activeFilter === 'staff' ? 'primary' : 'secondary'}
            onClick={() => filterUsers('staff')}
          >
            STAFF
          </Button>
          <Button 
            variant={activeFilter === 'customer' ? 'primary' : 'secondary'}
            onClick={() => filterUsers('customer')}
          >
            CLIENTES
          </Button>
        </ButtonGroup>
      </div>
      
      {loading ? (
        <p>Cargando usuarios...</p>
      ) : (
        <>
          {/* Información de paginación */}
          <PaginationInfo>
            Mostrando {indexOfFirstUser + 1} - {Math.min(indexOfLastUser, filteredUsers.length)} de {filteredUsers.length} usuarios
          </PaginationInfo>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Nombre</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>DNI</TableHeader>
                  <TableHeader>Rol</TableHeader>
                  <TableHeader>Acciones</TableHeader>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map(user => (
                <tr key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.dni}</TableCell>
                  <TableCell>
                    {user.role === 'admin' ? 'Administrador' : (user.role_name || 'Cliente')}
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <IconButton 
                        variant="edit" 
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleResetPassword(user)}
                      >
                        <Key />
                      </IconButton>
                      <IconButton 
                        variant="delete" 
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        <Trash />
                      </IconButton>
                    </ActionButtons>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>

        {/* Controles de paginación */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton
              variant="nav"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← Anterior
            </PaginationButton>
            
            <PaginationControls>
              {getPageNumbers().map((pageNumber, index) => (
                pageNumber === '...' ? (
                  <PaginationEllipsis key={`ellipsis-${index}`}>
                    ...
                  </PaginationEllipsis>
                ) : (
                  <PaginationButton
                    key={pageNumber}
                    variant="page"
                    active={currentPage === pageNumber}
                    onClick={() => handlePageChange(pageNumber as number)}
                  >
                    {pageNumber}
                  </PaginationButton>
                )
              ))}
            </PaginationControls>
            
            <PaginationButton
              variant="nav"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente →
            </PaginationButton>
          </PaginationContainer>
        )}
        </>
      )}
      
      {/* Modal para crear/editar usuario */}
      <Modal show={showModal}>
        <ModalContent>
          <h2>{editingUser ? 'Editar Usuario' : 'Agregar Usuario'}</h2>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Nombre</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>DNI</Label>
              <Input
                type="text"
                name="dni"
                value={formData.dni}
                onChange={handleInputChange}
                placeholder="Ingrese 8 dígitos"
                maxLength={8}
                pattern="[0-9]{8}"
                title="El DNI debe contener exactamente 8 dígitos numéricos"
                required
              />
            </FormGroup>
            
            {!editingUser && (
              <FormGroup>
                <Label>Contraseña</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  minLength={6}
                />
              </FormGroup>
            )}
            
            <FormGroup>
              <Label>Rol</Label>
              <Select name="selectedRole" value={selectedRole} onChange={handleInputChange}>
                <option value="">Cliente</option>
                <option value="admin">Administrador</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            </FormGroup>
            
            <ButtonGroup>
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary">
                {editingUser ? 'Actualizar' : 'Agregar'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
      
      {/* Modal para cambiar contraseña */}
      <Modal show={showPasswordModal}>
        <ModalContent>
          <h2>Cambiar Contraseña</h2>
          <Form onSubmit={handlePasswordSubmit}>
            <FormGroup>
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </FormGroup>
            
            <ButtonGroup>
              <Button type="button" variant="secondary" onClick={() => setShowPasswordModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary">
                Actualizar Contraseña
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default UsersTab;